import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { api, errCode, idemKey } from "@/lib/api";
import { ProductImage } from "@/components/store/ProductCard";
import { PaymentBadges } from "@/components/store/PaymentBadges";
import { useStoreInfo } from "@/lib/store";

const Step = ({ n, title, children }) => (
  <section className="card p-5 sm:p-6 space-y-4">
    <h2 className="flex items-center gap-3 font-semibold text-lg"><span className="w-7 h-7 rounded-full bg-[#0B0D0F] text-white text-sm flex items-center justify-center">{n}</span>{title}</h2>
    {children}
  </section>
);

export const Field = ({ label, testid, ...p }) => <label className="block"><span className="label">{label}</span><input data-testid={testid} className="input" {...p} /></label>;

export function Totals({ q }) {
  const { t, money } = useI18n();
  return (
    <div className="space-y-2.5 text-sm">
      <div className="flex justify-between"><span className="muted">{t("checkout.subtotal")}</span><span>{money(q.subtotal)}</span></div>
      <div className="flex justify-between"><span className="muted">{t("checkout.shipping")}</span><span data-testid="checkout-shipping">{q.shipping_fee ? money(q.shipping_fee) : t("checkout.free")}</span></div>
      {q.discount_total > 0 && <div className="flex justify-between text-[#16A34A]"><span>{t("checkout.savings")}</span><span>-{money(q.discount_total)}</span></div>}
      <div className="flex justify-between muted text-xs"><span>{t("checkout.vat_included", { pct: q.vat_pct })}</span><span>{money(q.vat_included)}</span></div>
      <div className="flex justify-between pt-3 border-t border-[#E5E7EB] text-base font-bold"><span>{t("checkout.total")}</span><span data-testid="checkout-total">{money(q.total)}</span></div>
    </div>
  );
}

export default function Checkout() {
  const { t, pick, money } = useI18n();
  const { items } = useCart();
  const { user } = useAuth();
  const info = useStoreInfo();
  const [q, setQ] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const key = useMemo(idemKey, []);
  const [f, setF] = useState({ name: "", email: "", phone: "", city: "", district: "", street: "", building: "", postal_code: "", notes: "" });
  useEffect(() => { if (user) setF((x) => ({ ...x, name: x.name || user.name, email: x.email || user.email, phone: x.phone || user.phone || "" })); }, [user]);
  useEffect(() => {
    if (!items.length) return;
    api.post("/checkout/quote", { items: items.map((i) => ({ product_id: i.product_id, variant_id: i.variant_id, qty: i.qty })) })
      .then((r) => { setQ(r.data); setErr(null); }).catch((e) => setErr(errCode(e)));
  }, [items]);
  const on = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, variant_id: i.variant_id, qty: i.qty })),
        customer: { name: f.name, email: f.email, phone: f.phone.replace(/\s/g, "") },
        shipping: { city: f.city, district: f.district, street: f.street, building: f.building, postal_code: f.postal_code, notes: f.notes },
        origin_url: window.location.origin, idempotency_key: key });
      window.location.href = data.redirect_url;
    } catch (e2) { toast.error(t(`errors.${errCode(e2)}`)); setBusy(false); }
  };
  if (!items.length) return <div className="container-x py-24 text-center space-y-4"><p className="muted">{t("cart.empty")}</p><Link to="/products" className="btn-dark">{t("cart.browse")}</Link></div>;
  return (
    <form onSubmit={submit} className="container-x py-8 sm:py-12 grid lg:grid-cols-[1fr_400px] gap-6 lg:gap-10" data-testid="checkout-page">
      <div className="space-y-5">
        <h1 className="text-3xl font-bold tracking-tight">{t("checkout.title")}</h1>
        {q?.payment_mode === "test" && <div data-testid="checkout-test-mode-banner" className="rounded-2xl bg-amber-50 border border-amber-200 p-4 flex gap-3 text-sm text-amber-900"><FlaskConical className="w-5 h-5 shrink-0" />{t("checkout.test_mode")}</div>}
        <Step n="1" title={t("checkout.contact")}>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label={t("checkout.name")} testid="checkout-name" required minLength={2} value={f.name} onChange={on("name")} autoComplete="name" />
            <Field label={t("checkout.phone")} testid="checkout-phone" required type="tel" inputMode="tel" pattern="^\+?[0-9 ]{9,16}$" placeholder="05XXXXXXXX" value={f.phone} onChange={on("phone")} autoComplete="tel" />
            <div className="sm:col-span-2"><Field label={t("checkout.email")} testid="checkout-email" required type="email" value={f.email} onChange={on("email")} autoComplete="email" /></div>
          </div>
        </Step>
        <Step n="2" title={t("checkout.address")}>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label={t("checkout.city")} testid="checkout-city" required minLength={2} value={f.city} onChange={on("city")} />
            <Field label={t("checkout.district")} testid="checkout-district" required minLength={2} value={f.district} onChange={on("district")} />
            <div className="sm:col-span-2"><Field label={t("checkout.street")} testid="checkout-street" required minLength={2} value={f.street} onChange={on("street")} /></div>
            <Field label={t("checkout.building")} testid="checkout-building" value={f.building} onChange={on("building")} />
            <Field label={t("checkout.postal")} testid="checkout-postal" inputMode="numeric" value={f.postal_code} onChange={on("postal_code")} />
          </div>
        </Step>
        <Step n="3" title={t("checkout.payment")}>
          <div data-testid="checkout-payment-section" className="rounded-xl border-2 border-[#2563EB] bg-[#2563EB]/5 p-4 space-y-3 text-sm">
            <div className="flex items-center gap-3"><Lock className="w-5 h-5 text-[#2563EB] shrink-0" /><div><p className="font-semibold">{t("checkout.method_card")}</p><p className="muted text-xs">{t("checkout.method_note", { provider: q?.payment_provider === "tap" ? "Tap Payments" : "Moyasar" })}</p></div></div>
            <PaymentBadges methods={info?.payment_methods} />
          </div>
          {q && !q.payment_ready && <p data-testid="checkout-payments-not-ready" className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-[#B91C1C]">{t("errors.payments_not_configured")}</p>}
        </Step>
      </div>
      <aside className="lg:sticky lg:top-24 h-fit card p-5 sm:p-6 space-y-5">
        <h2 className="font-semibold text-lg">{t("checkout.review")}</h2>
        {err && <p data-testid="checkout-error" className="text-sm text-[#DC2626]">{t(`errors.${err}`)}</p>}
        <div className="space-y-4 max-h-72 overflow-y-auto">
          {(q?.items || []).map((i) => (
            <div key={`${i.product_id}${i.variant_id}`} className="flex gap-3">
              <div className="w-14 h-14 rounded-lg overflow-hidden shrink-0"><ProductImage path={i.image} alt="" /></div>
              <div className="flex-1 min-w-0 text-sm"><p className="line-clamp-1">{pick(i.name)}</p><p className="muted text-xs">{i.variant_name && pick(i.variant_name)} × {i.qty}</p></div>
              <span className="text-sm font-medium">{money(i.line_total)}</span>
            </div>
          ))}
        </div>
        {q && <Totals q={q} />}
        <button data-testid="place-order-btn" disabled={!q || busy || !!err || !q.payment_ready} className="btn-primary w-full h-14 text-base"><Lock className="w-4 h-4" />{busy ? t("common.processing") : t("checkout.place_order")}</button>
        {q && !q.payment_ready && <p className="text-xs text-[#B91C1C] text-center">{t("errors.payments_not_configured")}</p>}
        <p className="text-xs muted text-center">{t("checkout.terms")} <Link to="/terms" className="underline">{t("legal.terms_title")}</Link> · <Link to="/policy" className="underline">{t("footer.refund_policy")}</Link> · <Link to="/privacy" className="underline">{t("legal.privacy_title")}</Link></p>
      </aside>
    </form>
  );
}
